import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo/SEO";
import { RelatedServicesSection } from "@/components/RelatedServicesSection";
import { CTASection } from "@/components/CTASection";
import { 
  Home, 
  Lightbulb, 
  Shield, 
  Music, 
  Thermometer, 
  Smartphone,
  Wifi,
  Battery,
  ArrowRight,
  CheckCircle,
  Star,
  Zap,
  Settings,
  Video,
  Volume2,
  Lock,
  Car,
  Brain,
  Gauge,
  TrendingUp,
  Users,
  Award,
  Clock,
  ArrowDown,
  MessageCircle,
  Eye,
  Database,
  Wrench
} from "lucide-react";
import { track } from "@/lib/analytics";
import { Link } from "react-router-dom";
import { BrandExplorer } from "@/components/BrandExplorer";
import { COMPATIBLE_BRANDS } from "@/data/compatibleBrands";
import { EnergyManagementSection } from "@/components/EnergyManagementSection";
import { DetailedFeaturesSection } from "@/components/DetailedFeaturesSections";
import { PrivacyTrustSection } from "@/components/PrivacyTrustSection";
import { SolViProcessSection } from "@/components/SolViProcessSection";
import { AdvertisementSections } from "@/components/AdvertisementSections";
import { SmartHomeFAQ } from "@/components/SmartHomeFAQ";
import { LoxoneComparison } from "@/components/LoxoneComparison";
import { SmartHomeCaseStudies } from "@/components/SmartHomeCaseStudies";
import LoxoneLogo from '/lovable-uploads/61ca03ab-7d73-4d3e-858b-4957ab9d692a.png';

const loxoneProducts = [
  {
    title: "Miniserver",
    description: "Het hart van uw Loxone Smart Home systeem",
    video: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Basics%20%26%20Accessories/100335%20-%20Miniserver/Images/vi-miniserver-overview-100335-miniserver.webm",
    features: ["Centraal controlesysteem", "Local processing", "Automatische updates", "24/7 betrouwbaarheid"],
    icon: Settings
  },
  {
    title: "Touch Pure Flex",
    description: "Futuristische bedieningspanelen met backlight",
    video: "https://pim.loxone.com/02%20Category%20Data/Controls/Touch_Flex_backlight_webshop_category.webm",
    features: ["Intuïtieve bediening", "Gepersonaliseerde interface", "Backlight technologie", "Muur- en tafeluitvoering"],
    icon: Smartphone
  },
  {
    title: "Audioserver",
    description: "Professionele multiroom audio oplossing",
    video: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/100428%20-%20Audioserver/Images/vi-overview-100428-audioserver.webm",
    features: ["Tot 14 zones", "Kristalheldere kwaliteit", "Streaming services", "Geïntegreerde versterkers"],
    icon: Volume2
  },
  {
    title: "EV Charging Wallbox",
    description: "Intelligente laadoplossingen voor elektrische voertuigen",
    video: "https://pim.loxone.com/02%20Category%20Data/Energy/Wallbox_webshop_category.webm",
    features: ["Smart charging", "Solar surplus laden", "Load balancing", "App controle"],
    icon: Car
  },
  {
    title: "Climate Control",
    description: "Geavanceerde klimaatbeheersing met slimme actuatoren",
    video: "https://pim.loxone.com/02%20Category%20Data/Climate%20%26%20Shading/Valve_Actuator_webshop_category.webm",
    features: ["Precision temperature control", "Energy optimization", "Zone-based heating", "Predictive algorithms"],
    icon: Thermometer
  },
  {
    title: "Intelligent Lighting",
    description: "Volledig geautomatiseerde verlichtingscontrole",
    video: "https://pim.loxone.com/02%20Category%20Data/Lighting/Lighting_webshop_category_v03.webm",
    features: ["Automatische bediening", "Energiebesparing", "Sfeerverlichting", "Aanwezigheidsdetectie"],
    icon: Lightbulb
  }
];

const smartHomeApplications = [
  {
    icon: Lightbulb,
    title: "Intelligent Lighting",
    description: "Verlichting die automatisch reageert op uw aanwezigheid en wensen"
  },
  {
    icon: Music,
    title: "Multiroom Audio",
    description: "Muziek door het hele huis met perfecte synchronisatie"
  },
  {
    icon: Thermometer,
    title: "Klimaatbeheersing",
    description: "Optimale temperatuur en luchtkwaliteit in elke ruimte"
  },
  {
    icon: Car,
    title: "EV Charging",
    description: "Slim laden van elektrische voertuigen op zonne-energie"
  },
  {
    icon: Shield,
    title: "Veiligheid & Toegang",
    description: "Complete beveiliging met slimme toegangscontrole"
  },
  {
    icon: Zap,
    title: "Energiebeheer",
    description: "Optimaal gebruik van zonnepanelen en thuisbatterijen"
  },
  {
    icon: Video,
    title: "Zonwering & Ramen",
    description: "Automatische zonwering voor comfort en privacy"
  },
  {
    icon: Brain,
    title: "AI-Learning",
    description: "Het systeem leert uw gewoontes en anticipeert uw behoeften"
  },
  {
    icon: Gauge,
    title: "Prestatie Monitoring",
    description: "Real-time inzicht in energieverbruik en systeem optimalisatie"
  }
];

const SmartHome = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Loxone Smart Home Elst | AI Domotica & Automatisering | SOL-VI Partner"
          description="🚀 Loxone Specialist | Complete smart home systemen Gelderland: intelligente verlichting, klimaat, beveiliging & energiebeheer ⚡ 40% besparing ✅ Cloudvrij & lokaal"
          keywords="loxone smart home Elst, gebouwautomatisering Gelderland, smart home systeem, domotica installatie Arnhem, loxone miniserver, intelligente verlichting, multiroom audio, smart home beveiliging, energiemanagement, loxone partner Nederland, KNX alternatief, gebouwbeheersysteem, domotica Betuwe"
          canonical="https://sol-vi.nl/smart-home"
          type="website"
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Loxone Smart Home Installatie",
              "description": "Professionele Loxone smart home installatie en configuratie door gecertificeerde partners in Gelderland",
              "provider": {
                "@type": "LocalBusiness",
                "name": "SOL-VI",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Elst",
                  "addressRegion": "Gelderland",
                  "addressCountry": "NL"
                }
              },
              "areaServed": ["Gelderland", "Overbetuwe", "Arnhem", "Nijmegen", "Elst"],
              "serviceType": "Smart Home Installation"
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Wat kost een Loxone smart home systeem?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Een Loxone systeem kost tussen €5.000-€25.000 afhankelijk van grootte en gewenste functionaliteiten. Voor een gemiddelde woning (€10.000-€15.000) verdient het systeem zichzelf terug door energiebesparingen."
                  }
                },
                {
                  "@type": "Question", 
                  "name": "Hoe betrouwbaar is Loxone?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Loxone heeft een uptime van 99.9% en werkt volledig lokaal zonder internetafhankelijkheid. Het systeem is gebouwd op industriële hardware en gebruikt geen cloud-diensten."
                  }
                }
              ]
            }
          ]}
        />
        
        {/* Enhanced Hero Section */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          <div className="absolute inset-0 bg-black">
            <video 
              autoPlay 
              muted 
              loop 
              playsInline
              className="w-full h-full object-cover opacity-30"
            >
              <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Basics%20%26%20Accessories/100335%20-%20Miniserver/Images/vi-miniserver-overview-100335-miniserver.webm" type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-background/80"></div>
          </div>
          <div className="relative container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-white">
              <div className="inline-flex items-center gap-3 bg-primary/20 backdrop-blur-sm text-primary-foreground px-6 py-3 rounded-full text-sm font-medium mb-8 border border-primary/30">
                <img src={LoxoneLogo} alt="Loxone" className="h-6 w-6" />
                <Star className="h-5 w-5" />
                Loxone Partner - Geïnstalleerd door SOL-VI
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Loxone: De Toekomst van{" "}
                <span className="text-primary bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                  Wonen en Werken
                </span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl mb-6 max-w-3xl mx-auto text-gray-100">
                In een wereld waar technologie steeds complexer wordt, biedt Loxone de ultieme oplossing: 
                <strong className="text-white"> één intelligent systeem dat 50.000 handelingen per jaar van je overneemt</strong>, 
                zodat jij meer tijd hebt voor wat echt belangrijk is.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-2xl font-bold text-primary">40%</div>
                  <div className="text-xs text-white/80">Energiebesparing</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-2xl font-bold text-primary">50k</div>
                  <div className="text-xs text-white/80">Handelingen/jaar</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-2xl font-bold text-primary">1</div>
                  <div className="text-xs text-white/80">Systeem voor alles</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="text-2xl font-bold text-primary">100%</div>
                  <div className="text-xs text-white/80">Privacy</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  size="lg" 
                  variant="hero"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow"
                  onClick={() => {
                    track("loxone_hero_calendly");
                    (window as any).Calendly?.initPopupWidget?.({
                      url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                      pageSettings: {
                        backgroundColor: 'ffffff',
                        hideEventTypeDetails: false,
                        hideLandingPageDetails: false,
                        primaryColor: 'ff7800',
                        textColor: '4d5055'
                      }
                    });
                  }}
                >
                  Plan Smart Home Advies
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" asChild>
                  <Link to="/contact">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Direct Contact
                  </Link>
                </Button>
              </div>
              
              <div className="text-sm text-white/80">
                ✓ Professionele installatie door gecertificeerde Loxone Partner experts
              </div>
            </div>
          </div>
        </section>

        {/* Problem Identification Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Herken je deze <span className="text-destructive">Frustraties</span>?
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
                  Moderne technologie belooft ons leven makkelijker te maken, maar in werkelijkheid wordt het vaak alleen maar complexer.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                <Card className="p-6 text-center border-destructive/20 hover:border-destructive/40 transition-colors">
                  <div className="bg-destructive/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="h-6 w-6 text-destructive" />
                  </div>
                  <h3 className="font-semibold mb-2">App Chaos</h3>
                  <p className="text-sm text-muted-foreground">
                    Meerdere apps voor verlichting, thermostaat, alarm en muziek. Een jungle van schakelaars en afstandsbedieningen.
                  </p>
                </Card>

                <Card className="p-6 text-center border-destructive/20 hover:border-destructive/40 transition-colors">
                  <div className="bg-destructive/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Zap className="h-6 w-6 text-destructive" />
                  </div>
                  <h3 className="font-semibold mb-2">Energieverspilling</h3>
                  <p className="text-sm text-muted-foreground">
                    Verwarming aan terwijl ramen open staan. Lichten die de hele dag branden. Apparaten in standby.
                  </p>
                </Card>

                <Card className="p-6 text-center border-destructive/20 hover:border-destructive/40 transition-colors">
                  <div className="bg-destructive/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-6 w-6 text-destructive" />
                  </div>
                  <h3 className="font-semibold mb-2">Veiligheidsrisico's</h3>
                  <p className="text-sm text-muted-foreground">
                    Losse beveiligingssystemen die niet communiceren. Geen overzicht wanneer je niet thuis bent.
                  </p>
                </Card>

                <Card className="p-6 text-center border-destructive/20 hover:border-destructive/40 transition-colors">
                  <div className="bg-destructive/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-6 w-6 text-destructive" />
                  </div>
                  <h3 className="font-semibold mb-2">Tijdverspilling</h3>
                  <p className="text-sm text-muted-foreground">
                    Dagelijks honderden knoppen indrukken en instellingen aanpassen. Kostbare tijd die je niet terugkrijgt.
                  </p>
                </Card>
              </div>

              <div className="text-center">
                <p className="text-lg font-medium text-muted-foreground mb-6">
                  <strong>Is er geen betere, slimmere manier?</strong>
                </p>
                <div className="text-4xl font-bold text-primary mb-4">JA, DIE IS ER.</div>
                <Button 
                  size="lg"
                  onClick={() => {
                    track("smarthome_problem_solution_cta");
                    document.getElementById('loxone-solution')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Ontdek de Oplossing
                  <ArrowDown className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Loxone Solution Section */}
        <section id="loxone-solution" className="py-20 bg-gradient-to-br from-primary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Star className="h-4 w-4" />
                  Welkom bij Loxone
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Het Intelligente Hart van <span className="text-primary">Jouw Gebouw</span>
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                  Loxone is een totaaloplossing die alle techniek in een gebouw samenbrengt en intelligent automatiseert. 
                  Het is vergelijkbaar met het menselijk zenuwstelsel: alle componenten worden centraal aangestuurd door 
                  het "brein" van het systeem: de <strong>Loxone Miniserver</strong>.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <Card className="overflow-hidden">
                    <div className="aspect-video bg-black relative">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover"
                      >
                        <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Basics%20%26%20Accessories/100335%20-%20Miniserver/Images/vi-miniserver-overview-100335-miniserver.webm" type="video/webm" />
                      </video>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm font-medium text-primary">Loxone Miniserver</span>
                      </div>
                    </div>
                  </Card>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">De Belofte van SOL-VI & Loxone:</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Home className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Ongeëvenaard Comfort</h4>
                        <p className="text-sm text-muted-foreground">De juiste sfeer op elk moment van de dag, volledig automatisch.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Maximale Energie-efficiëntie</h4>
                        <p className="text-sm text-muted-foreground">Bespaar tot 40% op je energiekosten door intelligent beheer.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Shield className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Uitzonderlijke Veiligheid</h4>
                        <p className="text-sm text-muted-foreground">Een allesomvattend beveiligingssysteem dat inbraak, brand en waterschade detecteert.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Smartphone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Eenvoud en Gebruiksgemak</h4>
                        <p className="text-sm text-muted-foreground">Weg met de chaos van schakelaars. Eén uniforme, intuïtieve bediening in elke ruimte.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  size="lg" 
                  variant="hero"
                  onClick={() => {
                    track("smarthome_solution_calendly");
                    (window as any).Calendly?.initPopupWidget?.({
                      url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                      pageSettings: {
                        backgroundColor: 'ffffff',
                        hideEventTypeDetails: false,
                        hideLandingPageDetails: false,
                        primaryColor: 'ff7800',
                        textColor: '4d5055'
                      }
                    });
                  }}
                >
                  Ontdek Jouw Smart Home Mogelijkheden
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Loxone Products Section */}
        <section id="products" className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Loxone Product Showcase
                </h2>
                <p className="text-lg text-muted-foreground">
                  Ontdek de kernproducten van het Loxone ecosysteem
                </p>
              </div>

              <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {loxoneProducts.map((product, index) => (
                  <Card key={index} className="overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-primary/10">
                    <div className="relative aspect-video bg-black">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      >
                        <source src={product.video} type="video/webm" />
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                          <div className="text-center">
                            <product.icon className="h-12 w-12 mx-auto mb-2" />
                            <p>Video niet ondersteund</p>
                          </div>
                        </div>
                      </video>
                      <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm rounded-full p-2 group-hover:bg-primary transition-colors">
                        <product.icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 group-hover:text-primary transition-colors">
                        {product.title}
                      </CardTitle>
                      <CardDescription className="text-sm">{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {product.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* New Enhanced Product Sections */}
        
        {/* Climate Control Section */}
        <section className="py-16 bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-950/20 dark:to-cyan-950/20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Thermometer className="h-4 w-4" />
                  Climate Control
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Intelligente <span className="text-primary">Klimaatbeheersing</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Perfecte temperatuur in elke ruimte met automatische aansturing van radiatoren, vloerverwarming en airco
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <Card className="overflow-hidden">
                    <div className="relative aspect-video bg-black">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover"
                      >
                        <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Climate/100602%20-%20Stellantrieb%20Tree/Images/vi-overview-100602-valve-actuator.webm" type="video/webm" />
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                          <div className="text-center">
                            <Thermometer className="h-12 w-12 mx-auto mb-2" />
                            <p>Loxone Valve Actuator Tree - Slimme radiatoraansturing</p>
                          </div>
                        </div>
                      </video>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm font-medium text-primary">Valve Actuator Tree</span>
                      </div>
                    </div>
                  </Card>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-4">Stelventiel Tree - Precisie Temperatuurregeling</h3>
                    <p className="text-muted-foreground mb-4">
                      De Loxone Valve Actuator Tree zorgt voor nauwkeurige temperatuurregeling per radiator of vloerverwarmingszone. 
                      Door automatische aansturing bespaart u tot 30% op uw verwarmingskosten.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">24V</div>
                      <div className="text-sm text-muted-foreground">Veilige spanning</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">±0.5°C</div>
                      <div className="text-sm text-muted-foreground">Precisie regeling</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">Tree</div>
                      <div className="text-sm text-muted-foreground">Eenvoudige bekabeling</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">30%</div>
                      <div className="text-sm text-muted-foreground">Energiebesparing</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">Automatische aanpassing per ruimte</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">Compatible met alle gangbare radiatoren</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">Geruisloze werking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Control Section */}
        <section className="py-16 bg-gradient-to-br from-slate-50/50 to-gray-50/50 dark:from-slate-950/20 dark:to-gray-950/20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Shield className="h-4 w-4" />
                  Access Control
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Slimme <span className="text-primary">Toegangscontrole</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Veilig en gebruiksvriendelijk toegangsbeheer met NFC technologie en moderne design
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-4">NFC Code Touch Tree - Premium Toegang</h3>
                    <p className="text-muted-foreground mb-4">
                      De NFC Code Touch Tree in antraciet combineert NFC technologie met een verlichte toetsenbord op 
                      glas voor ultieme veiligheid en gebruiksgemak. Perfecte integratie in modern interieur.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">NFC</div>
                      <div className="text-sm text-muted-foreground">Contactloze toegang</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">PIN</div>
                      <div className="text-sm text-muted-foreground">Numerieke codes</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">LED</div>
                      <div className="text-sm text-muted-foreground">Verlichte toetsen</div>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-primary">IP54</div>
                      <div className="text-sm text-muted-foreground">Weer bestendig</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">NFC tags, kaarten en sleutelhangers</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">Gelogde toegangsgeschiedenis</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span className="text-sm">Tijdgestuurde toegangsrechten</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Card className="overflow-hidden">
                    <div className="relative aspect-video bg-black">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover"
                      >
                        <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Access/100480%20-%20NFC%20Code%20Touch%20Tree%20Anthrazit/Images/vi-overview-100480-nfc-code-touch-anthrazit.webm" type="video/webm" />
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                          <div className="text-center">
                            <Shield className="h-12 w-12 mx-auto mb-2" />
                            <p>NFC Code Touch Tree - Premium toegangscontrole</p>
                          </div>
                        </div>
                      </video>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm font-medium text-primary">NFC Code Touch Tree</span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Professional Audio Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-950/20 dark:to-pink-950/20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Music className="h-4 w-4" />
                  Professional Audio
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Multiroom <span className="text-primary">Audio Systemen</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Professionele audiosystemen voor het hele huis met Master-Client architectuur en streaming services
                </p>
              </div>

              {/* Audio Banner Video */}
              <div className="mb-12">
                <Card className="overflow-hidden">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com/02%20Category%20Data/Audio/Master%20-%20Client%20webshop%20category.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <div className="text-center">
                          <Music className="h-16 w-16 mx-auto mb-4" />
                          <p className="text-xl">Loxone Audio - Master-Client Systeem</p>
                        </div>
                      </div>
                    </video>
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                      <span className="text-sm font-medium text-primary">Multiroom Audio Overview</span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Audio Products Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {/* Install Speaker 7 Master */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610149%20-%20Install%20Speaker%207%20Master/Images/vi-overview-610149-install-speaker-7-master.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Volume2 className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">Install Speaker 7 Master</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Actieve luidspreker met coaxiaal geïntegreerde tweeter. Kan tot 20 Client speakers aansturen.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Vermogen:</strong> 30W RMS</div>
                      <div><strong>Bereik:</strong> 45Hz-20kHz</div>
                      <div><strong>Clients:</strong> Tot 20 speakers</div>
                      <div><strong>Voeding:</strong> 24V Tree</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Wall Speaker Black */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610001%20-%20WALL%20Speaker/Images/vi-overview-610001-wallspeaker-anthrazit.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Volume2 className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">Wall Speaker Antraciet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Elegante wandluidspreker in antraciet finish voor discrete integratie in moderne interieurs.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Design:</strong> Antraciet</div>
                      <div><strong>Montage:</strong> Inbouw/opbouw</div>
                      <div><strong>Zone:</strong> Per ruimte</div>
                      <div><strong>Controle:</strong> App & Touch</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Wall Speaker White */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610141%20-%20WALL%20Speaker%20white/Images/vi-overview-610141-wallspeaker-white.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Volume2 className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">Wall Speaker Wit</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Stijlvolle witte wandluidspreker die perfect integreert in lichte en moderne woninginrichtingen.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Design:</strong> Wit</div>
                      <div><strong>Montage:</strong> Inbouw/opbouw</div>
                      <div><strong>Zone:</strong> Per ruimte</div>
                      <div><strong>Controle:</strong> App & Touch</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Audio Features */}
              <div className="bg-gradient-to-r from-purple-100/50 to-pink-100/50 dark:from-purple-900/20 dark:to-pink-900/20 p-8 rounded-xl">
                <h3 className="text-2xl font-bold mb-6 text-center">Waarom Loxone Audio?</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">20</div>
                    <div className="text-sm text-muted-foreground">Speakers per keten</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">∞</div>
                    <div className="text-sm text-muted-foreground">Streaming services</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">1</div>
                    <div className="text-sm text-muted-foreground">Kabel per zone</div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <Button variant="outline" asChild>
                    <Link to="/audio">
                      <Music className="mr-2 h-4 w-4" />
                      Ontdek Audio Systemen
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Lighting Section */}
        <section className="py-16 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Lightbulb className="h-4 w-4" />
                  Advanced Lighting
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Intelligente <span className="text-primary">LED Verlichting</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  16 miljoen kleuren, automatische aanpassingen en energiebesparende RGBW technologie
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {/* LED Spot RGBW Tree Anthracite */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Lighting/100334%20-%20LED%20Spot%20RGBW%20Tree%20Anthrazit/Images/vi-overview-100334-led-spot-rgbw-tree-anthrazit.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Lightbulb className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">LED Spot RGBW Tree Antraciet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Premium inbouwspot met 16 miljoen kleuren en warm wit. Perfecte kleurafstemming en dimfunctionaliteit.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Kleuren:</strong> 16 miljoen</div>
                      <div><strong>Warm wit:</strong> 2700K-6500K</div>
                      <div><strong>Dimbaar:</strong> 0-100%</div>
                      <div><strong>Tree:</strong> Eenvoudige installatie</div>
                    </div>
                  </CardContent>
                </Card>

                {/* LED Spot RGBW Tree White */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Lighting/100330%20-%20LED%20Spot%20RGBW%20Tree%20Wei%C3%9F/Images/vi-overview-100330-led-spot-rgbw-tree-white.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Lightbulb className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">LED Spot RGBW Tree Wit</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Elegante witte uitvoering van de RGBW Tree spot voor moderne en lichte interieurs.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Kleuren:</strong> 16 miljoen</div>
                      <div><strong>Warm wit:</strong> 2700K-6500K</div>
                      <div><strong>Dimbaar:</strong> 0-100%</div>
                      <div><strong>Design:</strong> Minimalistisch wit</div>
                    </div>
                  </CardContent>
                </Card>

                {/* LED Ceiling Light RGBW Air */}
                <Card className="overflow-hidden group hover:shadow-lg transition-all">
                  <div className="relative aspect-video bg-black">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Lighting/100287%20-%20LED%20Ceiling%20Light%20RGBW%20Air%20Anthrazit/Images/vi-overview-100287-ceiling-light-air-anthrazit.webm" type="video/webm" />
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Lightbulb className="h-8 w-8" />
                      </div>
                    </video>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">LED Plafondlamp RGBW Air</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Stijlvolle plafondverlichting met draadloze Air technologie en volledig regelbare RGBW kleuren.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><strong>Technologie:</strong> Draadloos Air</div>
                      <div><strong>RGBW:</strong> Alle kleuren</div>
                      <div><strong>Montage:</strong> Plafond opbouw</div>
                      <div><strong>Bereik:</strong> 100m open veld</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Lighting Features */}
              <div className="bg-gradient-to-r from-amber-100/50 to-orange-100/50 dark:from-amber-900/20 dark:to-orange-900/20 p-8 rounded-xl">
                <h3 className="text-2xl font-bold mb-6 text-center">Slimme Verlichting Voordelen</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">80%</div>
                    <div className="text-sm text-muted-foreground">Energiebesparing</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">16M</div>
                    <div className="text-sm text-muted-foreground">Kleuropties</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">∞</div>
                    <div className="text-sm text-muted-foreground">Lichtscènes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">Auto</div>
                    <div className="text-sm text-muted-foreground">Dag/nacht ritme</div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <Button variant="outline" asChild>
                    <Link to="/elektra">
                      <Lightbulb className="mr-2 h-4 w-4" />
                      LED Verlichting Diensten
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Smart Home Applications */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Smart Home <span className="text-primary">Toepassingen</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Ontdek de eindeloze mogelijkheden van uw Loxone smart home systeem
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {smartHomeApplications.map((app, index) => (
                  <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <CardContent className="p-6">
                      <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <app.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{app.title}</h3>
                      <p className="text-muted-foreground text-sm">{app.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Smart Energy Management Section */}
        <EnergyManagementSection />

        {/* Compatible Brands Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm text-primary px-6 py-3 rounded-full text-sm font-medium mb-6 border border-primary/20">
                  <Award className="h-5 w-5" />
                  80+ Premium Merken & Integraties
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Complete Merkcompatibiliteit
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Ontdek alle {COMPATIBLE_BRANDS.length}+ merken die naadloos integreren met uw Loxone smart home systeem. 
                  Van premium audiomerken tot professionele klimaatbeheersing - alles werkt perfect samen.
                </p>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">{COMPATIBLE_BRANDS.length}+</div>
                  <p className="text-sm text-muted-foreground">Compatible Merken</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">11</div>
                  <p className="text-sm text-muted-foreground">Categorieën</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">{COMPATIBLE_BRANDS.filter(b => b.integrationLevel === 'Direct').length}</div>
                  <p className="text-sm text-muted-foreground">Directe Integraties</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">{COMPATIBLE_BRANDS.filter(b => b.popularity === 'High').length}</div>
                  <p className="text-sm text-muted-foreground">Populaire Merken</p>
                </div>
              </div>

              {/* Interactive Brand Explorer */}
              <BrandExplorer />
            </div>
          </div>
        </section>

        {/* FlipHTML5 Smart Home Book Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Smart Home Gids
                </h2>
                <p className="text-lg text-muted-foreground">
                  Ontdek alles over smart home technologie in onze interactieve gids
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="bg-card rounded-lg shadow-lg overflow-hidden">
                  <iframe 
                    src="https://online.fliphtml5.com/wkrve/zsfb/" 
                    width="100%" 
                    height="600" 
                    seamless
                    scrolling="no" 
                    frameBorder="0" 
                    allowTransparency={true}
                    allowFullScreen={true}
                    className="w-full"
                    title="Smart Home Gids"
                  ></iframe>
                </div>
              </div>

              {/* CTA Section */}
              <div className="text-center mt-12">
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-8">
                  <h3 className="text-2xl font-bold mb-4">
                    Interesse in Smart Home Technologie?
                  </h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Neem contact met ons op en ontdek hoe wij uw woning kunnen transformeren 
                    tot een intelligent smart home systeem. Wij helpen u verder.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg"
                      onClick={() => {
                        track("smart_home_book_contact");
                        (window as any).Calendly?.initPopupWidget?.({
                          url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
                        });
                      }}
                    >
                      Plan Adviesgesprek
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/contact">Contact Opnemen</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ROI & Benefits Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Smart Home ROI
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    Een Loxone Smart Home betaalt zichzelf terug door energiebesparingen, 
                    verhoogde woningwaarde en dagelijks comfort.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>Tot 30% energiebesparing door intelligente automatisering</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>5-15% hogere woningwaarde</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>Lagere verzekeringspremies door veiligheidssystemen</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                      <span>Comfort en gemak zijn niet in geld uit te drukken</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-2xl">
                  <h3 className="text-2xl font-bold mb-6 text-center">Voorbeeld Besparing</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span>Energiekosten per jaar</span>
                      <span className="font-bold">€2.400</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span>30% Smart Home besparing</span>
                      <span className="font-bold text-green-600">-€720</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-primary text-white rounded-lg">
                      <span>Nieuwe energiekosten</span>
                      <span className="font-bold">€1.680</span>
                    </div>
                    <div className="text-center text-sm text-muted-foreground mt-4">
                      Terugverdientijd: 3-5 jaar
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EMS System - Software Focus Section */}
        <section className="py-20 bg-gradient-to-br from-background to-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Hoe ons EMS systeem het <span className="text-primary">Verschil</span> maakt?
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  De software van ons Energy Management System is de echte kracht achter de intelligent automatisering.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <h3 className="text-2xl font-bold mb-6">De Software</h3>
                  <p className="text-lg text-muted-foreground mb-6">
                    De hardware van ons EMS is slechts het topje van de ijsberg. In feite zijn we voornamelijk een 
                    softwareproducent, aangezien <strong className="text-primary">85% van ons ontwikkelingswerk 
                    naar softwaretools gaat</strong>.
                  </p>
                  
                  <p className="text-lg text-muted-foreground mb-8">
                    Ons energie management systeem en de talrijke functies worden pas mogelijk gemaakt door onze software.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Brain className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Intelligente Algoritmes</h4>
                        <p className="text-sm text-muted-foreground">
                          Geavanceerde software analyseert energiepatronen en optimaliseert automatisch uw verbruik.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Database className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Data-Driven Beslissingen</h4>
                        <p className="text-sm text-muted-foreground">
                          Machine learning zorgt ervoor dat het systeem continu leert en verbetert.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg">
                      <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Wrench className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">Continue Updates</h4>
                        <p className="text-sm text-muted-foreground">
                          Regular software updates brengen nieuwe functies en verbeteringen naar uw systeem.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="order-1 lg:order-2">
                  <div className="relative">
                    <img 
                      src="/lovable-uploads/a3621ac4-6b57-4867-8c55-ada144f1d70e.png" 
                      alt="Hardware en software verhouding in EMS systeem - 15% hardware, 85% software"
                      className="w-full max-w-md mx-auto drop-shadow-2xl"
                    />
                    <div className="absolute -bottom-4 -right-4 bg-primary text-white px-4 py-2 rounded-lg shadow-lg">
                      <div className="text-sm font-medium">85% Software</div>
                      <div className="text-xs opacity-90">15% Hardware</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-16 text-center">
                <div className="bg-card border border-border rounded-lg p-8 max-w-4xl mx-auto">
                  <h3 className="text-xl font-bold mb-4">Waarom Software de Sleutel is</h3>
                  <p className="text-lg text-muted-foreground mb-6">
                    Terwijl anderen focussen op hardware-specificaties, investeren wij in de intelligentie die 
                    uw energiesysteem echt slim maakt. Onze software-expertise zorgt voor:
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center">
                      <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <TrendingUp className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Maximale Efficiency</h4>
                      <p className="text-sm text-muted-foreground">
                        Intelligente optimalisatie van energiestromen
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Eye className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Voorspellende Analyse</h4>
                      <p className="text-sm text-muted-foreground">
                        Anticiperen op uw energiebehoeften
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Settings className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="font-semibold mb-2">Automatische Aanpassingen</h4>
                      <p className="text-sm text-muted-foreground">
                        Zelfregulerende systemen zonder handmatige input
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg"
                      onClick={() => {
                        track("ems_software_contact");
                        (window as any).Calendly?.initPopupWidget?.({
                          url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
                        });
                      }}
                    >
                      Ontdek Ons EMS Systeem
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/contact">Meer Informatie</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <SmartHomeCaseStudies />

        {/* Comparison Section */}
        <LoxoneComparison />

        {/* FAQ Section */}
        <SmartHomeFAQ />

        <RelatedServicesSection
          currentService="smarthome"
          title="Combineer Smart Home met Duurzame Energie"
          description="Maximaal rendement met slim energiebeheer"
        />

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default SmartHome;